/**
 * One drawing per exercise type, so a movement wears the same mark in the table, the card and the
 * creator.
 *
 * **Transcribed from the mobile app's `Icon.glyphs`** — `typeStrength`, `typeBodyweight`,
 * `typeCardio`, `typeMobility`, `typeConditioning`, `typePlyometric` — and the 17 September 2026
 * specimen's `TYPE_GLYPH` is the same six paths, which is what makes the KANON shot and the app
 * comparable at all. A 24-box, `currentColor`, stroke 2 with round caps and joins: the CALLER
 * supplies the colour (`exerciseTypeColour`'s `--type-ink`) and the box, because the size and the
 * ground differ per surface — 17px on a 30px wash square in a row, 26px on a tile in the creator.
 *
 * `custom` has no drawing of its own and borrows `strength`'s. That is the phone's behaviour and it
 * is the honest one: a trainer's own exercise is not a seventh KIND of training, so inventing a
 * mark for it would say something the taxonomy does not.
 *
 * Not in `ExerciseGlyphs.tsx` beside it, deliberately: that file answers "there is no photograph of
 * this movement" with one barbell, and this one answers "what kind of training is it". Two
 * questions, and merging them would give the barbell a meaning it does not have.
 */

import type { ExerciseTypeColourKey } from './exerciseTypeColour';

interface GlyphProps {
  readonly className?: string;
}

/** The six paths, by the key that draws them. `custom` is absent — see the header. */
const PATHS: Readonly<Record<Exclude<ExerciseTypeColourKey, 'custom'>, string>> = {
  strength: 'M4 9v6M7.5 6.5v11M7.5 12h9M16.5 6.5v11M20 9v6',
  bodyweight: 'M12 7v6.5M12 13.5 8.5 21M12 13.5 15.5 21M6 9.5l6 1.5 6-1.5',
  cardio: 'M3 12h3.4l1.7-5 3.3 11 2-6 1.3 3H21',
  mobility: 'M9 6.5c0 3 0 4 2.5 5l3.5 1.5M9 11.5 6 21M9 11.5 12 17',
  conditioning: 'M13 2 4.5 13.5H11l-1 8.5L19.5 9.5H13z',
  plyometric: 'M7 8 12 3 17 8M5.5 15.5 12 9 18.5 15.5M3.5 21h17',
};

/** Two of the six carry a head, which a `<path>` cannot draw. */
const HEADS: Readonly<Partial<Record<ExerciseTypeColourKey, { cx: number; cy: number; r: number }>>> =
  {
    bodyweight: { cx: 12, cy: 4.5, r: 2.2 },
    mobility: { cx: 9, cy: 4.5, r: 2 },
  };

/**
 * The type's mark, in `currentColor`.
 *
 * `aria-hidden`, always: the glyph is one of two channels and never the only one — a row's type is
 * named in the cell's own accessible text, and the panel prints the word beside the dot. A drawing
 * that repeated the word would have a listener hear the type twice.
 */
export function ExerciseTypeGlyph({
  type,
  className,
}: GlyphProps & { readonly type: ExerciseTypeColourKey | null }) {
  const key = type === null || type === 'custom' ? 'strength' : type;
  const head = HEADS[key];
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {head !== undefined && <circle cx={head.cx} cy={head.cy} r={head.r} />}
      <path d={PATHS[key]} />
    </svg>
  );
}
